import { useState } from "react";
import {
  useGetList,
  useCreate,
  useDelete,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";

export const WhitelistSection = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { data: entries, refetch } = useGetList("signup_whitelist", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "created_at", order: "DESC" },
  });
  const [create] = useCreate();
  const [deleteOne] = useDelete();
  const [type, setType] = useState<"email" | "domain">("email");
  const [value, setValue] = useState("");

  const handleAdd = () => {
    const trimmed = value.trim();
    if (!trimmed) return;

    if (type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      notify("crm.settings.whitelist.invalid_email", {
        type: "error",
        messageArgs: { _: "Invalid email format" },
      });
      return;
    }
    if (type === "domain" && trimmed.startsWith("@")) {
      notify("crm.settings.whitelist.no_at_prefix", {
        type: "error",
        messageArgs: {
          _: "Enter bare domain (e.g. example.com), not @example.com",
        },
      });
      return;
    }

    create(
      "signup_whitelist",
      { data: { type, value: trimmed, created_by: identity?.id } },
      {
        onSuccess: () => {
          setValue("");
          refetch();
        },
        onError: (error: any) => {
          if (error?.message?.includes("duplicate")) {
            notify("crm.settings.whitelist.duplicate", {
              type: "error",
              messageArgs: { _: "This entry already exists" },
            });
          } else {
            notify(error?.message || "Error", { type: "error" });
          }
        },
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteOne(
      "signup_whitelist",
      { id },
      {
        onSuccess: () => refetch(),
        onError: (error: any) => {
          notify(error?.message || "Failed to delete entry", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-muted-foreground">
        {translate("crm.settings.sections.whitelist", {
          _: "Signup Whitelist",
        })}
      </h2>
      <p className="text-sm text-muted-foreground">
        {translate("crm.settings.whitelist.description", {
          _: "Control which emails or domains can sign up via Google OAuth.",
        })}
      </p>

      <div className="flex gap-2">
        <Select
          value={type}
          onValueChange={(v) => setType(v as "email" | "domain")}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="email">
              {translate("crm.settings.whitelist.type_email", { _: "Email" })}
            </SelectItem>
            <SelectItem value="domain">
              {translate("crm.settings.whitelist.type_domain", {
                _: "Domain",
              })}
            </SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={type === "email" ? "user@example.com" : "example.com"}
          className="flex-1"
        />
        <Button type="button" onClick={handleAdd}>
          {translate("ra.action.add", { _: "Add" })}
        </Button>
      </div>
      {type === "domain" && (
        <p className="text-xs text-muted-foreground">
          {translate("crm.settings.whitelist.domain_hint", {
            _: "Enter bare domain (e.g. example.com), not @example.com",
          })}
        </p>
      )}

      <div className="space-y-1">
        {entries?.map((entry) => (
          <div
            key={entry.id}
            className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted"
          >
            <div className="flex gap-2 items-center">
              <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                {entry.type}
              </span>
              <span>{entry.value}</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(entry.id as number)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {entries?.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            {translate("crm.settings.whitelist.empty", {
              _: "No entries. All Google signups are currently allowed.",
            })}
          </p>
        )}
      </div>
    </div>
  );
};
